import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Bird, ScanLine, Fingerprint, Package, Smartphone, Check, ChevronRight } from "lucide-react";

const IMG = {
  sozinha: "https://static.prod-images.emergentagent.com/jobs/01926a1e-4895-486f-9e22-5f463bd4d108/images/7f541244186bac2f53582f0f853be3f96d605dd59052ddf2708fee8d0305931a.jpeg",
  cores: "https://static.prod-images.emergentagent.com/jobs/01926a1e-4895-486f-9e22-5f463bd4d108/images/98423b6d84f6bc24455c6f98bc3f888f959b97c383670f6060a505de87bc3c48.jpeg",
  na_ave: "https://static.prod-images.emergentagent.com/jobs/01926a1e-4895-486f-9e22-5f463bd4d108/images/75d1461109773eef4c2288716feacd5fa8c514bfeb05070e01ffbd484acf6517.jpeg",
  closeup: "https://static.prod-images.emergentagent.com/jobs/01926a1e-4895-486f-9e22-5f463bd4d108/images/814b810389e82c63f82b862878008e846f30e46d5e93054396a4f2abbef8d0cc.jpeg",
  celular: "https://static.prod-images.emergentagent.com/jobs/01926a1e-4895-486f-9e22-5f463bd4d108/images/9917e5d465c66432846725e5eb852144d7b0273c322880e2cf77168895e0b28a.jpeg",
};

const STEPS = [
  { icon: Package, title: "1. Escolha a anilha", text: "Modelo, tamanho e cor ideais para a espécie da sua ave." },
  { icon: Bird, title: "2. Cadastre a ave", text: "Nome, espécie, foto e demais informações no perfil digital." },
  { icon: Fingerprint, title: "3. Receba sua anilha", text: "Identificação individual gravada, entregue no seu endereço." },
  { icon: ScanLine, title: "4. Consulte pelo código 2D", text: "Qualquer leitor abre o perfil digital exclusivo da ave." },
];

export default function AvesID() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [pets, setPets] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [petSel, setPetSel] = useState("");
  const [phone, setPhone] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [customCode, setCustomCode] = useState("");
  const [lookup, setLookup] = useState("");
  const [addr, setAddr] = useState({ cep: "", rua: "", numero: "", complemento: "", bairro: "", cidade: "Joinville" });
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/anilhas").then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    if (user) api.get("/pets").then((r) => setPets(r.data)).catch(() => {});
  }, [user]);

  const birds = pets.filter((p) => p.species === "Ave");

  const openBuy = (p) => {
    if (!user) return navigate("/login?next=/aves");
    setSel(p);
    setSize(p.sizes?.[0] || "");
    setColor(p.colors?.[0] || "");
    setQty(1);
    setBuyOpen(true);
  };

  const confirm = async () => {
    if (!petSel) return toast.error("Escolha a ave que receberá a anilha");
    if (!phone) return toast.error("Informe seu telefone de contato");
    if (!addr.cep || !addr.rua || !addr.numero) return toast.error("Informe CEP, rua e número para a entrega");
    setBuying(true);
    try {
      const r = await api.post("/payments/checkout", {
        kind: "anilha", anilha_id: sel.product_id, pet_id: petSel,
        size, color, quantity: qty, phone, address: addr, custom_code: customCode,
      });
      if (r.data.init_point) { window.location.assign(r.data.init_point); return; }
      toast.success("Pedido registrado! Avisaremos quando a confecção começar.", { duration: 6000 });
      setBuyOpen(false);
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no checkout");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div data-testid="aves-page">
      <section className="bg-brand-graphite text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-white/10 text-brand-mint text-xs font-semibold px-3 py-1.5 rounded-full mb-6" data-testid="aves-badge">
              <Bird className="w-3.5 h-3.5" /> Identificação para Aves — lançamento
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-heading leading-tight">
              A identificação da sua ave, <span className="text-brand-mint">agora também digital</span>
            </h1>
            <p className="text-white/70 mt-5 leading-relaxed max-w-lg">
              A Anilha 2D Inteligente conecta a identificação física da ave a um perfil digital na PetMachineHub.
              Cada unidade possui uma identificação exclusiva, permitindo consultar as informações cadastradas
              da ave por meio do código 2D gravado na anilha.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => document.getElementById("modelos")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full px-7 h-12 font-semibold" data-testid="want-anilha-btn">
                Quero minha Anilha 2D <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("modelos")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full px-7 h-12 border-white/30 text-white hover:bg-white/10 bg-transparent" data-testid="see-models-btn">
                Ver modelos disponíveis
              </Button>
            </div>
            <p className="text-white/40 text-xs mt-6">Produto oficial PetMachine Hub — produção sob encomenda com gravação individual.</p>
          </div>
          <div className="grid grid-cols-2 gap-4 animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <img src={IMG.sozinha} alt="Anilha 2D Inteligente" className="rounded-2xl object-cover w-full h-56 shadow-2xl" />
            <img src={IMG.na_ave} alt="Anilha instalada em calopsita" className="rounded-2xl object-cover w-full h-56 shadow-2xl mt-8" />
            <img src={IMG.closeup} alt="Close do código 2D gravado" className="rounded-2xl object-cover w-full h-56 shadow-2xl" />
            <img src={IMG.cores} alt="Cores disponíveis" className="rounded-2xl object-cover w-full h-56 shadow-2xl mt-8" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="how-it-works">
        <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-graphite text-center">Como funciona</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border card-hover text-center animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className={`w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-4 ${i % 2 === 0 ? "bg-brand-coralLight text-brand-coral" : "bg-brand-mintLight text-brand-mintDark"}`}>
                <s.icon className="w-6 h-6" />
              </div>
              <p className="font-heading font-semibold text-brand-graphite">{s.title}</p>
              <p className="text-sm text-muted-foreground mt-2">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="modelos" className="max-w-6xl mx-auto px-6 py-10" data-testid="anilha-models">
        <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-graphite text-center">Modelos disponíveis</h2>
        <p className="text-center text-muted-foreground mt-2 text-sm">Produção sob encomenda com gravação individual. Entrega em Joinville e região.</p>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {products.map((p) => (
            <div key={p.product_id} className={`bg-white rounded-3xl border-2 card-hover flex flex-col overflow-hidden ${p.tier === "2d" ? "border-brand-coral shadow-lg" : "border-transparent"}`} data-testid={`anilha-card-${p.product_id}`}>
              {p.photos?.[0] && <img src={p.photos[0]} alt={p.name} className="w-full h-44 object-cover" />}
              <div className="p-6 flex flex-col flex-1">
                {p.tier === "2d" && <span className="self-start bg-brand-coral text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Recomendada</span>}
                <p className="font-heading font-bold text-xl text-brand-graphite">{p.name}</p>
                <p className="text-xs text-brand-mintDark font-medium mt-0.5">{p.tagline}</p>
                <p className="font-heading font-extrabold text-3xl text-brand-graphite mt-3">
                  R$ {Number(p.price).toFixed(2).replace(".", ",")}<span className="text-sm font-normal text-muted-foreground"> /un</span>
                </p>
                <p className="text-xs text-muted-foreground mt-2">{p.description}</p>
                <ul className="text-sm space-y-1.5 mt-4 flex-1">
                  {p.features?.map((f) => <li key={f} className="flex items-start gap-2 text-brand-graphite"><Check className="w-4 h-4 text-brand-mint mt-0.5 shrink-0" />{f}</li>)}
                </ul>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {p.sizes?.map((s) => <span key={s} className="text-[10px] bg-brand-cream border rounded-full px-2 py-0.5">{s}</span>)}
                  {p.colors?.map((c) => <span key={c} className="text-[10px] bg-brand-mintLight text-brand-mintDark rounded-full px-2 py-0.5">{c}</span>)}
                </div>
                <Button onClick={() => openBuy(p)} disabled={!p.available}
                  className={`w-full rounded-full mt-5 h-11 font-semibold ${p.tier === "2d" ? "bg-brand-coral hover:bg-brand-coralDark text-white" : "bg-brand-graphite hover:bg-black text-white"}`}
                  data-testid={`anilha-buy-${p.product_id}`}>
                  {p.available ? "Comprar" : "Indisponível"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="bg-white rounded-[2rem] border overflow-hidden grid lg:grid-cols-2" data-testid="demo-profile-section">
          <div className="p-8 lg:p-12 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 bg-brand-mintLight text-brand-mintDark text-xs font-semibold px-3 py-1.5 rounded-full mb-5 self-start">
              <Smartphone className="w-3.5 h-3.5" /> Demonstração
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading text-brand-graphite">O código 2D abre o perfil digital da ave</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Ao escanear o código da anilha, qualquer pessoa acessa o perfil digital exclusivo da ave:
              foto, espécie, identificação, criador e contato do proprietário — sempre respeitando o que o tutor escolher tornar público.
            </p>
            <Link to="/anilha/PMH-000001" data-testid="demo-profile-link">
              <Button className="bg-brand-mint hover:bg-brand-mintDark text-white rounded-full px-7 h-11 mt-6 font-semibold">
                Ver perfil demo (PMH-000001) <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <div className="mt-6 bg-brand-cream rounded-2xl p-4" data-testid="bird-lookup-card">
              <p className="text-sm font-semibold text-brand-graphite">Encontrou uma ave com anilha?</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">Digite o código gravado na anilha (ex.: MY 026 ou PMH-000001) — funciona mesmo sem ler o código 2D.</p>
              <div className="flex gap-2">
                <Input value={lookup} onChange={(e) => setLookup(e.target.value)} placeholder="Código da anilha" data-testid="bird-code-input" />
                <Button onClick={() => { if (lookup.trim()) navigate(`/anilha/${encodeURIComponent(lookup.trim())}`); }} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full shrink-0" data-testid="bird-code-search-btn">Buscar</Button>
              </div>
            </div>
          </div>
          <img src={IMG.celular} alt="Perfil digital da ave no celular" className="w-full h-full object-cover max-h-[420px]" />
        </div>
      </section>

      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Comprar — {sel?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3" data-testid="anilha-order-form">
            {birds.length > 0 ? (
              <div>
                <Label>Vincular a qual ave? *</Label>
                <Select value={petSel} onValueChange={setPetSel}>
                  <SelectTrigger className="mt-1" data-testid="anilha-pet-select"><SelectValue placeholder="Escolha a ave" /></SelectTrigger>
                  <SelectContent>{birds.map((p) => <SelectItem key={p.pet_id} value={p.pet_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground bg-brand-cream rounded-xl p-3">
                Você ainda não tem aves cadastradas. <Link to="/app" className="text-brand-coral font-semibold">Cadastre sua ave</Link> antes de comprar a anilha.
              </p>
            )}
            <div><Label>Seu telefone de contato *</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(47) 99999-9999" className="mt-1" data-testid="anilha-phone-input" /></div>
            <div>
              <Label>Código do criador (opcional)</Label>
              <Input value={customCode} onChange={(e) => setCustomCode(e.target.value)} placeholder="Ex.: MY 026, WG 148, PMH 1234" className="mt-1" data-testid="anilha-custom-code-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Tem um código tradicional de criador? Ele será gravado na anilha junto com o código 2D — e quem encontrar a ave pode digitá-lo na busca, mesmo sem ler o QR.</p>
            </div>
            <div className="bg-brand-mintLight rounded-xl p-3 text-xs text-brand-graphite">
              <p><strong>Identificação:</strong> a anilha recebe um código exclusivo PMH com código 2D que abre o perfil digital da ave (nome, espécie, seu contato e mais).</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tamanho</Label>
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger className="mt-1" data-testid="anilha-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{sel?.sizes?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cor / acabamento</Label>
                <Select value={color} onValueChange={setColor}>
                  <SelectTrigger className="mt-1" data-testid="anilha-color-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{sel?.colors?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Quantidade</Label>
              <Input type="number" min="1" max="50" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="mt-1" data-testid="anilha-qty-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CEP *</Label><Input value={addr.cep} onChange={(e) => setAddr({ ...addr, cep: e.target.value })} placeholder="89200-000" className="mt-1" data-testid="anilha-addr-cep" /></div>
              <div><Label>Cidade</Label><Input value={addr.cidade} onChange={(e) => setAddr({ ...addr, cidade: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Rua *</Label><Input value={addr.rua} onChange={(e) => setAddr({ ...addr, rua: e.target.value })} className="mt-1" data-testid="anilha-addr-rua" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Número *</Label><Input value={addr.numero} onChange={(e) => setAddr({ ...addr, numero: e.target.value })} className="mt-1" data-testid="anilha-addr-numero" /></div>
              <div><Label>Bairro</Label><Input value={addr.bairro} onChange={(e) => setAddr({ ...addr, bairro: e.target.value })} className="mt-1" /></div>
            </div>
            <Button onClick={confirm} disabled={buying} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="anilha-confirm-btn">
              {buying ? "Processando..." : `Confirmar pedido — R$ ${sel ? (Number(sel.price) * qty).toFixed(2).replace(".", ",") : ""}`}
            </Button>
            <p className="text-xs text-center text-muted-foreground">Prazo de produção estimado: {sel?.production_days} dias.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
